import { useContext } from 'react';
import { GridColDef } from "@mui/x-data-grid";
import { IconButton, Chip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { PaginasCustomizadasContext } from "./paginas-customizadas-context";
import { ACTION_TYPES, initialState } from "./paginas-customizadas-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { ICustomPagesListRequest } from "../../../../model/admin-portal/request/custom-pages-list-request";
import { IAddUpdatePageSectionRequest, IAddUpdateSectionItemRequest } from "../../../../model/admin-portal/request/add-update-custom-page-request";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";

export const usePaginasCustomizadasState = () => {
    const context = useContext(PaginasCustomizadasContext);
    if (!context) {
        throw new Error('usePaginasCustomizadasState must be used within PaginasCustomizadasProvider');
    }
    return context.paginasCustomizadasState;
};

export const usePaginasCustomizadasDispatch = () => {
    const context = useContext(PaginasCustomizadasContext);
    if (!context) {
        throw new Error('usePaginasCustomizadasDispatch must be used within PaginasCustomizadasProvider');
    }
    return context.paginasCustomizadasDispatch;
};

export const usePaginasCustomizadasController = () => {
    const {
        dataSearchRequest,
        dataRequest,
        dataPaginatedListResponse,
        filesMap
    } = usePaginasCustomizadasState();
    const paginasCustomizadasDispatch = usePaginasCustomizadasDispatch();
    const { showBackDrop, showMessage, showDialog } = useBaseLayoutController();

    const loadData = async (request: ICustomPagesListRequest) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setIsLoadingData,
            payload: true,
        });

        try {
            const response = await AdminPortalAppService.listCustomPages(request);
            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setDataPaginatedListResponse,
                payload: response,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar páginas customizadas.' });
        } finally {
            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setIsLoadingData,
                payload: false,
            });
        }
    };

    const getData = async (id: number) => {
        showBackDrop(true);
        try {
            const response = await AdminPortalAppService.getCustomPage(id);

            // Mapear response para dataRequest format
            const requestData = {
                id: response.id,
                title: response.title,
                metaDescription: response.metaDescription || '',
                metaKeywords: response.metaKeywords || '',
                publicationDate: new Date(response.publicationDate),
                published: response.published,
                order: response.order || null,
                sections: response.sections.map(section => ({
                    id: section.id,
                    type: section.type,
                    title: section.title || '',
                    content: section.content || '',
                    url: section.url || '',
                    imageFileName: section.imageFileName || '',
                    order: section.order,
                    items: section.items.map(item => ({
                        id: item.id,
                        title: item.title,
                        subtitle: item.subtitle || '',
                        description: item.description || '',
                        url: item.url || '',
                        imageFileName: item.imageFileName || '',
                        order: item.order,
                        file: null
                    })),
                    file: null
                }))
            };

            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setDataResponse,
                payload: response,
            });

            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setDataRequest,
                payload: requestData,
            });

            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setShowAddEdit,
                payload: true,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar página.' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        if (!dataRequest.title || dataRequest.title.length === 0) {
            return false;
        }

        if (!dataRequest.sections || dataRequest.sections.length === 0) {
            showMessage({ type: 'error', message: 'É necessário adicionar pelo menos uma seção.' });
            return false;
        }

        for (const section of dataRequest.sections) {
            if (!section.type || section.order === undefined) {
                showMessage({ type: 'error', message: 'Todas as seções devem ter tipo e ordem definidos.' });
                return false;
            }

            // Type 1 requires content
            if (section.type === 1 && (!section.content || section.content.length === 0)) {
                showMessage({ type: 'error', message: 'Seções do tipo "Texto Livre" devem ter conteúdo.' });
                return false;
            }

            // Type 6 requires URL
            if (section.type === 6 && (!section.url || section.url.length === 0)) {
                showMessage({ type: 'error', message: 'Seções do tipo "Vídeo Embed" devem ter URL.' });
                return false;
            }

            // Types 2-5 with items require at least title
            if ([2, 3, 4, 5].includes(section.type) && section.items && section.items.length > 0) {
                for (const item of section.items) {
                    if (!item.title || item.title.length === 0) {
                        showMessage({ type: 'error', message: 'Todos os itens devem ter título.' });
                        return false;
                    }
                }
            }
        }

        return true;
    };

    const save = async () => {
        if (!validFields()) {
            return;
        }

        showBackDrop(true);

        try {
            // Attach files from filesMap to sections and items
            const sectionsWithFiles = dataRequest.sections.map((section, sIdx) => {
                const sectionFile = filesMap.get(`section-${sIdx}`);
                const itemsWithFiles = section.items?.map((item, iIdx) => {
                    const itemFile = filesMap.get(`section-${sIdx}-item-${iIdx}`);
                    return {
                        ...item,
                        file: itemFile || null
                    };
                });

                return {
                    ...section,
                    items: itemsWithFiles,
                    file: sectionFile || null
                };
            });

            const payload = {
                ...dataRequest,
                sections: sectionsWithFiles
            };

            await AdminPortalAppService.addUpdateCustomPage(payload);
            showMessage({ type: 'success', message: 'Página salva com sucesso!' });

            // Reset form and reload list
            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setShowAddEdit,
                payload: false,
            });

            paginasCustomizadasDispatch({
                type: ACTION_TYPES.setDataRequest,
                payload: initialState.dataRequest,
            });

            paginasCustomizadasDispatch({
                type: ACTION_TYPES.clearFilesMap
            });

            await loadData(dataSearchRequest);
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao salvar página.' });
        } finally {
            showBackDrop(false);
        }
    };

    const deleteData = (id: number) => {
        showDialog({
            title: 'Confirmar Exclusão',
            message: 'Deseja realmente excluir esta página customizada?',
            onYesClick: async () => {
                showBackDrop(true);
                try {
                    await AdminPortalAppService.deleteCustomPage(id);
                    showMessage({ type: 'success', message: 'Página excluída com sucesso!' });
                    await loadData(dataSearchRequest);
                } catch (error) {
                    showMessage({ type: 'error', message: 'Erro ao excluir página.' });
                } finally {
                    showBackDrop(false);
                }
            },
            onNoClick: () => { },
            open: true,
            onClose: () => { }
        });
    };

    const handleOnChangeForm = (field: string, value: any) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                [field]: value
            }
        });
    };

    const onChangePagination = (page: number, pageSize: number) => {
        // Proteção: evita re-disparo se valores não mudaram
        if (dataSearchRequest?.page === page &&
            dataSearchRequest?.pageSize === pageSize) {
            return;
        }

        const newSearchRequest = {
            ...dataSearchRequest,
            page: page, // Backend agora usa 0-based indexing
            pageSize: pageSize
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataSearchRequest,
            payload: newSearchRequest,
        });

        loadData(newSearchRequest);
    };

    // Section management
    const addNewSection = () => {
        const newSection: IAddUpdatePageSectionRequest = {
            id: null,
            type: 1,
            title: '',
            content: '',
            url: '',
            imageFileName: '',
            order: dataRequest.sections.length + 1,
            items: [],
            file: null
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: [...dataRequest.sections, newSection]
            }
        });
    };

    const removeSectionByIndex = (sectionIndex: number) => {
        const updatedSections = dataRequest.sections.filter((_, idx) => idx !== sectionIndex);

        // Remove associated files
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.removeFileForSection,
            payload: { sectionIndex }
        });

        // Remove files for all items in this section
        if (dataRequest.sections[sectionIndex]?.items) {
            dataRequest.sections[sectionIndex].items?.forEach((_, itemIndex) => {
                paginasCustomizadasDispatch({
                    type: ACTION_TYPES.removeFileForItem,
                    payload: { sectionIndex, itemIndex }
                });
            });
        }

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    const updateSection = (sectionIndex: number, field: string, value: any) => {
        const updatedSections = [...dataRequest.sections];
        updatedSections[sectionIndex] = {
            ...updatedSections[sectionIndex],
            [field]: value
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    // Item management
    const addNewItemToSection = (sectionIndex: number) => {
        const section = dataRequest.sections[sectionIndex];
        const newItem: IAddUpdateSectionItemRequest = {
            id: null,
            title: '',
            subtitle: '',
            description: '',
            url: '',
            imageFileName: '',
            order: (section.items?.length || 0) + 1,
            file: null
        };

        const updatedSections = [...dataRequest.sections];
        updatedSections[sectionIndex] = {
            ...section,
            items: [...(section.items || []), newItem]
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    const removeItemFromSection = (sectionIndex: number, itemIndex: number) => {
        const section = dataRequest.sections[sectionIndex];
        const updatedItems = section.items?.filter((_, idx) => idx !== itemIndex) || [];

        // Remove associated file
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.removeFileForItem,
            payload: { sectionIndex, itemIndex }
        });

        const updatedSections = [...dataRequest.sections];
        updatedSections[sectionIndex] = {
            ...section,
            items: updatedItems
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    const updateItem = (sectionIndex: number, itemIndex: number, field: string, value: any) => {
        const updatedSections = [...dataRequest.sections];
        const section = updatedSections[sectionIndex];
        const updatedItems = [...(section.items || [])];

        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            [field]: value
        };

        updatedSections[sectionIndex] = {
            ...section,
            items: updatedItems
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    // File management
    const getFileForSection = (sectionIndex: number): File | null => {
        return filesMap.get(`section-${sectionIndex}`) || null;
    };

    const getFileForItem = (sectionIndex: number, itemIndex: number): File | null => {
        return filesMap.get(`section-${sectionIndex}-item-${itemIndex}`) || null;
    };

    const setFileForSection = (sectionIndex: number, file: File | null) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setFileForSection,
            payload: { sectionIndex, file }
        });
    };

    const setFileForItem = (sectionIndex: number, itemIndex: number, file: File | null) => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setFileForItem,
            payload: { sectionIndex, itemIndex, file }
        });
    };

    // Drag and drop reordering
    const reorderSections = (startIndex: number, endIndex: number) => {
        const result = Array.from(dataRequest.sections);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        // Update order field for all sections
        const updatedSections = result.map((section, index) => ({
            ...section,
            order: index + 1
        }));

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    const reorderItems = (sectionIndex: number, startIndex: number, endIndex: number) => {
        const section = dataRequest.sections[sectionIndex];
        const items = Array.from(section.items || []);
        const [removed] = items.splice(startIndex, 1);
        items.splice(endIndex, 0, removed);

        // Update order field for all items
        const updatedItems = items.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        const updatedSections = [...dataRequest.sections];
        updatedSections[sectionIndex] = {
            ...section,
            items: updatedItems
        };

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...dataRequest,
                sections: updatedSections
            }
        });
    };

    // DataGrid columns
    const getColumnsGrid = (): GridColDef[] => {
        return [
            {
                field: 'title',
                headerName: 'Título',
                flex: 1,
                minWidth: 200
            },
            {
                field: 'slug',
                headerName: 'Slug',
                flex: 1,
                minWidth: 200
            },
            {
                field: 'published',
                headerName: 'Status',
                width: 130,
                renderCell: (params) => (
                    <Chip
                        label={params.value ? 'Publicado' : 'Rascunho'}
                        color={params.value ? 'success' : 'default'}
                        size="small"
                    />
                )
            },
            {
                field: 'publicationDate',
                headerName: 'Data de Publicação',
                width: 180,
                renderCell: (params) => {
                    const date = new Date(params.value);
                    return date.toLocaleDateString('pt-BR');
                }
            },
            {
                field: 'actions',
                headerName: 'Ações',
                width: 120,
                sortable: false,
                renderCell: (params) => (
                    <>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => getData(params.row.id)}
                            title="Editar"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => deleteData(params.row.id)}
                            title="Excluir"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </>
                )
            }
        ];
    };

    const openNewForm = () => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: initialState.dataRequest
        });

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataResponse,
            payload: null
        });

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.clearFilesMap
        });

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setShowAddEdit,
            payload: true
        });
    };

    const closeForm = () => {
        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setShowAddEdit,
            payload: false
        });

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: initialState.dataRequest
        });

        paginasCustomizadasDispatch({
            type: ACTION_TYPES.clearFilesMap
        });
    };

    return {
        loadData,
        getData,
        save,
        deleteData,
        validFields,
        handleOnChangeForm,
        onChangePagination,
        getColumnsGrid,
        addNewSection,
        removeSectionByIndex,
        updateSection,
        addNewItemToSection,
        removeItemFromSection,
        updateItem,
        getFileForSection,
        getFileForItem,
        setFileForSection,
        setFileForItem,
        openNewForm,
        closeForm,
        reorderSections
    };
};
